// Static fixture data for Client-Side Demo Preview Mode (Feb 2026).
//
// Payload shapes MUST match the live backend contracts so components render
// without conditional branches. Sources:
//   • /api/matches                       — routes/discover.py :: get_matches
//   • /api/likes-received                — routes/discover.py :: get_likes_received
//   • /api/chat/{match_id}               — routes/chat.py :: get_chat
//   • /api/profile-views                 — routes/store.py :: who_viewed_me
//
// Counts (from mobile handoff spec):
//   14 matches (12 live + 2 ghost — 1 deactivated, 1 deleted)
//   20 inbound likes
//   24 messages spread across the first few matches
//   Handful of profile views
//
// Fixtures assume the demo user is Premium so /likes-received returns
// hydrated profiles (not the free-tier blurred count).

const now = new Date();
const iso = (minutesAgo) => new Date(now.getTime() - minutesAgo * 60_000).toISOString();
const isoDays = (daysAgo) => new Date(now.getTime() - daysAgo * 86_400_000).toISOString();

const PHOTOS = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1546961342-1e3b5b0ebf9f?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1500048993953-d23a436266cf?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1521252659862-eec69941b071?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1544723795-3fb6469f5b39?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1552699611-e2c208d5d9cf?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1520295187453-cf4ea54b1a7b?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1524638067-feff89bee4d4?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&auto=format&fit=crop',
];

const NAMES = [
  ['Ava', 30], ['Nora', 28], ['Sofia', 32], ['Elena', 29],
  ['Maya', 27], ['Jules', 31], ['Priya', 33], ['Kai', 26],
  ['Rowan', 34], ['Bea', 29], ['Aria', 30], ['Zoe', 28],
  ['Iris', 32], ['Luna', 31],
];

const BIOS = [
  'Sunday hiker · Napa on weekends · Bake sourdough badly',
  'Product design + oat lattes. Ask me about pottery.',
  'Chef in a past life. Trying to make my dog Instagram-famous.',
  'PhD in a topic I never talk about. Bring me to a used bookstore.',
  'Rock climber weekends. Reads too many Substacks.',
  'Ex-lawyer, current painter. Two cats named after philosophers.',
  'Traveling more than my visa allows. Late-night ramen fan.',
  'Startup founder resting between reps. Big plant person.',
  'Grew up on a farm. Now I run half marathons badly.',
  'Long walks, hot coffee, cold plunges. No small talk.',
  'Concert person. If it\'s indie I\'ve probably seen them.',
  'Retired competitive swimmer. Currently learning Portuguese.',
];

// -------- MATCHES ---------------------------------------------------------

const _rawMatches = NAMES.map(([name, age], i) => {
  // Match → Meet → Keep lifecycle by index — same mapping as chatFor() so the
  // list bar + chat header stay consistent. See chatFor for the full slice map.
  const [stage, canMeet, canMet, canKeep, mm, mmet, mk, cycle] =
      i < 3   ? ['matched',       true,  false, false, null, null, null, 0]
    : i < 5   ? ['meet_pending',  false, false, false, null, null, null, 0]
    : i < 7   ? ['meeting',       false, true,  false, iso(120), null, null, 0]
    : i < 10  ? ['met',           false, false, true,  iso(240), iso(120), null, 0]
    :           ['kept',          false, false, false, iso(720), iso(600), iso(480), 1];
  return {
    match_id: `demo_match_${String(i).padStart(2, '0')}`,
    compatibility_score: 65 + ((i * 7) % 28),           // 65..92
    v2_compatibility_score: 65 + ((i * 7) % 28),
    v2_dealbreaker_flags: i === 4 ? ['kids_mismatch'] : [],
    v2_score_version: '2.0.0',
    created_at: isoDays(i * 1.5),
    other_user: {
      user_id: `demo_user_${String(i).padStart(2, '0')}`,
      display_name: name,
      photo_url: PHOTOS[i % PHOTOS.length],
      badges: i % 3 === 0 ? ['verified'] : [],
      age,
      bio: BIOS[i % BIOS.length],
      is_seed: false,
      // Ghost Match contract — live rows default.
      is_active: true,
      unavailable_reason: null,
      is_deleted: false,
      deleted_at: null,
      status: 'active',
    },
    last_message: null,
    last_call: null,
    lifecycle: {
      lifecycle_stage: stage,
      my_meet_intent: !canMeet && stage !== 'matched',
      my_met_confirm: stage === 'met' || stage === 'kept',
      my_keep_intent: stage === 'kept',
      mutual_meet_at: mm, mutual_met_at: mmet, mutual_keep_at: mk,
      keep_cycle: cycle,
      can_meet: canMeet, can_confirm_met: canMet, can_keep: canKeep,
    },
  };
});

// Ghost row #1 — deactivated (30-day reactivation window)
_rawMatches[8] = {
  match_id: 'demo_match_ghost_deact',
  compatibility_score: 78,
  v2_compatibility_score: 78,
  v2_dealbreaker_flags: [],
  v2_score_version: '2.0.0',
  created_at: isoDays(22),
  other_user: {
    user_id: 'demo_ghost_user_deact',
    display_name: 'Former match',
    photo_url: null,
    badges: [],
    age: null,
    bio: '',
    is_seed: false,
    is_active: false,
    unavailable_reason: 'deactivated',
    is_deleted: false,
    deleted_at: null,
    status: 'inactive',
  },
  last_message: null,
  last_call: null,
};

// Ghost row #2 — hard-deleted
_rawMatches[12] = {
  match_id: 'demo_match_ghost_deleted',
  compatibility_score: 71,
  v2_compatibility_score: 71,
  v2_dealbreaker_flags: [],
  v2_score_version: '2.0.0',
  created_at: isoDays(45),
  other_user: {
    user_id: 'demo_ghost_user_deleted',
    display_name: 'Former match',
    photo_url: null,
    badges: [],
    age: null,
    bio: '',
    is_seed: false,
    is_active: false,
    unavailable_reason: 'deleted',
    is_deleted: true,
    deleted_at: isoDays(30),
    status: 'deleted',
  },
  last_message: null,
  last_call: null,
};

// -------- MESSAGES --------------------------------------------------------
// 24 messages spread across the first 5 live matches (roughly 5 each).
// Each message uses the shape returned by GET /api/chat/{match_id}: sender_id,
// text, created_at, kind, read_by.

const ME_UID = 'demo_me';

function messagesFor(matchIdx, otherUid) {
  const base = matchIdx * 60;                                    // stagger by chat
  const linesForOther = [
    'Hey! Loved your prompt about pottery. Are you self-taught?',
    'I\'m free Saturday afternoon if you want to grab a coffee.',
    'Ha same. Napa is my go-to. Have you tried Ashes & Diamonds?',
    'How did you get into that? Sounds so specific.',
    'Ok now I need the sourdough backstory 😅',
  ];
  const linesForMe = [
    'Self-taught + a lot of YouTube 😄',
    'Saturday works. 3pm at Sightglass?',
    'Not yet! On the list now — thanks for the tip.',
    'Long story — I\'ll tell you over drinks.',
    'You do NOT want the sourdough backstory. It\'s cursed.',
  ];
  const out = [];
  const count = Math.min(linesForOther.length, linesForMe.length);
  for (let j = 0; j < count; j++) {
    out.push({
      message_id: `demo_msg_${matchIdx}_${j*2}`,
      sender_id: otherUid,
      text: linesForOther[j],
      created_at: iso(base - j*8 - 4),
      kind: 'text',
      read_by: [otherUid, ME_UID],
    });
    out.push({
      message_id: `demo_msg_${matchIdx}_${j*2+1}`,
      sender_id: ME_UID,
      text: linesForMe[j],
      created_at: iso(base - j*8),
      kind: 'text',
      read_by: [ME_UID],                                          // gated on Free
    });
  }
  return out;
}

const _messagesByMatch = {};
// Populate a spread across all lifecycle stages so the Matches list bar looks
// visually varied in demo mode (matched / meet_pending / meeting / met / kept).
for (const idx of [0, 1, 2, 3, 4, 5, 7, 10, 11]) {
  const m = _rawMatches[idx];
  if (!m) continue;
  _messagesByMatch[m.match_id] = messagesFor(idx, m.other_user.user_id);
}
// Total: 5 chats × 10 msgs each? Trim to 24 total across chats.
let _msgTotal = Object.values(_messagesByMatch).reduce((s, a) => s + a.length, 0);
while (_msgTotal > 24) {
  // Trim from the tail of the last chat.
  const keys = Object.keys(_messagesByMatch);
  const last = _messagesByMatch[keys[keys.length - 1]];
  if (!last.length) { _messagesByMatch[keys[keys.length - 1]] = undefined; break; }
  last.pop();
  _msgTotal--;
}

// Hydrate `last_message` on the matches so the Matches grid preview isn't blank.
for (const m of _rawMatches) {
  const bucket = _messagesByMatch[m.match_id];
  if (bucket && bucket.length) {
    const tail = bucket[bucket.length - 1];
    m.last_message = {
      text: tail.text, sender_id: tail.sender_id,
      created_at: tail.created_at, kind: tail.kind,
    };
  }
}

// -------- LIKES-RECEIVED --------------------------------------------------
// 20 inbound likes. Shape mirrors /api/likes-received Premium response:
// `profiles[]` each with liked_at + is_super + compatibility_score + is_seed.

const LIKE_NAMES = [
  ['Sasha', 29], ['Camila', 31], ['Sky', 27], ['Mira', 30], ['Juno', 28],
  ['Lena', 33], ['Vivi', 26], ['Nadia', 32], ['Wren', 29], ['Hana', 31],
  ['Tess', 27], ['Lila', 30], ['Alma', 34], ['Gia', 28], ['Riya', 29],
  ['Cora', 32], ['Naya', 26], ['Simi', 30], ['Elke', 33], ['Ida', 31],
];

const _likes = LIKE_NAMES.map(([name, age], i) => ({
  user_id: `demo_liker_${String(i).padStart(2, '0')}`,
  display_name: name,
  age,
  photo_url: PHOTOS[(i + 4) % PHOTOS.length],
  bio: BIOS[i % BIOS.length],
  compatibility_score: 60 + ((i * 5) % 35),
  liked_at: iso(i * 90),
  is_super: i < 3,                                    // 3 super likes at the top
  is_seed: false,
  badges: i % 4 === 0 ? ['verified'] : [],
}));

// -------- PROFILE VIEWS ---------------------------------------------------

const _views = LIKE_NAMES.slice(0, 12).map(([name, age], i) => ({
  user_id: `demo_viewer_${String(i).padStart(2, '0')}`,
  display_name: name,
  age,
  photo_url: PHOTOS[(i + 8) % PHOTOS.length],
  badges: i % 5 === 0 ? ['verified'] : [],
  is_seed: false,
  viewed_at: iso(i * 45),
  location: { city: ['SF', 'NYC', 'Austin', 'LA'][i % 4], country: 'US' },
}));

// -------- EXPORTED RESPONSES ---------------------------------------------

export const FIXTURES = {
  // GET /api/matches
  matches: {
    matches: _rawMatches,
    total: _rawMatches.length,
    offset: 0,
    limit: 30,
    has_more: false,
  },

  // GET /api/likes-received
  likesReceived: {
    tier: 'premium',
    total: _likes.length,
    profiles: _likes,
    locked: false,
    offset: 0,
    limit: 30,
    has_more: false,
  },

  // GET /api/profile-views
  profileViews: {
    tier: 'premium',
    locked: false,
    total: _views.length,
    viewers: _views,
  },

  // GET /api/chat/{match_id} — factory per match_id
  chatFor(matchId) {
    const match = _rawMatches.find(m => m.match_id === matchId);
    const msgs = _messagesByMatch[matchId] || [];
    if (!match) return null;
    // Feb 2026 · Match → Meet → Keep lifecycle demo state. Cycle through stages
    // by match index so mobile can see every UI state without seeding a DB:
    //   idx 0-2 → matched · idx 3-4 → meet_pending · idx 5-6 → meeting
    //   idx 7-9 → met · idx 10-13 → kept
    const idx = _rawMatches.indexOf(match);
    const [stage, canMeet, canMet, canKeep, mm, mmet, mk, cycle] =
      idx < 3   ? ['matched',       true,  false, false, null, null, null, 0]
    : idx < 5   ? ['meet_pending',  false, false, false, null, null, null, 0]
    : idx < 7   ? ['meeting',       false, true,  false, iso(120), null, null, 0]
    : idx < 10  ? ['met',           false, false, true,  iso(240), iso(120), null, 0]
    :             ['kept',          false, false, false, iso(720), iso(600), iso(480), 1];
    return {
      match: { match_id: matchId, user1_id: ME_UID, user2_id: match.other_user.user_id,
               created_at: match.created_at },
      other_user: {
        user_id: match.other_user.user_id,
        display_name: match.other_user.display_name,
        photo_url: match.other_user.photo_url,
        badges: match.other_user.badges,
        is_seed: match.other_user.is_seed,
      },
      messages: msgs,
      my_tier: 'premium',
      lifecycle: {
        lifecycle_stage: stage,
        my_meet_intent: !canMeet && (stage !== 'matched'),
        my_met_confirm: stage === 'met' || stage === 'kept',
        my_keep_intent: stage === 'kept',
        mutual_meet_at: mm, mutual_met_at: mmet, mutual_keep_at: mk,
        keep_cycle: cycle,
        can_meet: canMeet, can_confirm_met: canMet, can_keep: canKeep,
      },
    };
  },
};

export const FIXTURE_STATS = {
  matches: _rawMatches.length,
  liveMatches: _rawMatches.filter(m => m.other_user.is_active).length,
  ghostMatches: _rawMatches.filter(m => !m.other_user.is_active).length,
  likes: _likes.length,
  messages: _msgTotal,
  views: _views.length,
};
